import { Button, IconButton, SvgIcon, TextField, Tooltip } from '@mui/material';
import {
  RiAddLine,
  RiArrowGoBackLine,
  RiDeleteBinLine,
} from '@remixicon/react';
import { useEffect, useMemo, useRef } from 'react';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import InputHeader from '../../../components/InputHeader';
import { useLanguage } from '../../../contexts/language';
import { FormValues } from '../../../hooks/useForm';
import { Language, TranslationRecord } from '../../../types/language';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';

interface IslandFormValues extends FormValues {
  deletedPortIdsString: string;
  islandNameEnglish: string;
  islandNameIrish: string;
  // Allows an indeterminate amount of fields
  [key: PropertyKey]: string;
}

const createPortValueKey = (
  order: number,
  nameField: 'nameEnglish' | 'nameIrish',
): string => ['port', order.toString(), nameField].join('_');

const parsePortValueKey = (
  portValueKey: string,
): { order: number; nameField: 'nameEnglish' | 'nameIrish' } => {
  const [, order, nameField] = portValueKey.split('_');
  return {
    order: Number(order),
    nameField: nameField as 'nameEnglish' | 'nameIrish',
  };
};

const groupPortValueKeys = (
  values: IslandFormValues,
): Array<{ order: number; nameEnglishKey: string; nameIrishKey: string }> => {
  const portsValues: Record<PropertyKey, string> = { ...values };
  delete portsValues.islandNameEnglish;
  delete portsValues.islandNameIrish;
  delete portsValues.deletedPortIdsString;

  const portValueKeysMap: Record<
    number,
    { order: number; nameEnglishKey: string; nameIrishKey: string }
  > = {};

  Object.keys(portsValues).forEach(portValueKey => {
    const { order, nameField } = parsePortValueKey(portValueKey);
    if (!portValueKeysMap[order]) {
      portValueKeysMap[order] = {
        order,
        nameEnglishKey: '',
        nameIrishKey: '',
      };
    }
    if (nameField === 'nameEnglish') {
      portValueKeysMap[order].nameEnglishKey = portValueKey;
    } else if (nameField === 'nameIrish') {
      portValueKeysMap[order].nameIrishKey = portValueKey;
    }
  });
  return Object.values(portValueKeysMap);
};

export interface IslandFormInputValue {
  names: TranslationRecord;
  ports: Array<{
    id: string;
    names: TranslationRecord;
  }>;
}

export interface IslandFormSubmittedValue {
  names: TranslationRecord;
  ports: Array<{
    id: string | null;
    names: TranslationRecord;
  }>;
}

export interface IslandFormPageProps {
  initialValue: IslandFormInputValue | null;
  onSubmit: (value: IslandFormSubmittedValue, callback: () => void) => void;
  onCancel?: () => void;
  isEditingByDefault?: boolean;
  isPerformingOperation: boolean;
  isFetching: boolean;
  onIslandNameChange?: (newName: string) => void;
}

const IslandForm = ({
  onCancel,
  onSubmit,
  onIslandNameChange,
  isEditingByDefault = false,
  initialValue,
  isPerformingOperation,
  isFetching,
}: IslandFormPageProps) => {
  const { language, t } = useLanguage();

  const orderMaxIndex = useRef(0);

  const shouldInputsBeDisabled = isFetching || isPerformingOperation;

  const { initialValues, orderToPortIdMap } = useMemo(() => {
    const orderToPortIdMap: Record<number, string> = {};

    const initialValues: IslandFormValues = {
      deletedPortIdsString: '',
      islandNameEnglish: '',
      islandNameIrish: '',
    };
    if (!initialValue) {
      initialValues[createPortValueKey(0, 'nameEnglish')] = '';
      initialValues[createPortValueKey(0, 'nameIrish')] = '';

      orderMaxIndex.current = 1;

      return {
        initialValues,
        orderToPortIdMap,
      };
    }

    initialValues.islandNameEnglish = initialValue.names[Language.EN];
    initialValues.islandNameIrish = initialValue.names[Language.GA];

    initialValue.ports.forEach((port, index) => {
      initialValues[createPortValueKey(index, 'nameEnglish')] =
        port.names[Language.EN];
      initialValues[createPortValueKey(index, 'nameIrish')] =
        port.names[Language.GA];

      orderToPortIdMap[index] = port.id;
    });

    orderMaxIndex.current = initialValue.ports.length;

    return { initialValues, orderToPortIdMap };
  }, [initialValue]);

  const {
    formState,
    materialInputHelpers: { getBaseProps, getTextFieldProps },
  } = useIotsAdminForm(
    {
      isEditingByDefault,
      initialValues,
      onSubmit: (values, callback) => {
        const { deletedPortIdsString, islandNameEnglish, islandNameIrish } =
          values;

        const deletedPortIds = new Set(deletedPortIdsString.split(';'));
        const groupedPortValueKeys = groupPortValueKeys(values);

        const returnedValue: IslandFormSubmittedValue = {
          names: {
            [Language.EN]: islandNameEnglish,
            [Language.GA]: islandNameIrish,
          },
          ports: [],
        };

        groupedPortValueKeys.forEach(group => {
          const existingPortId = orderToPortIdMap[group.order] ?? null;
          // Only those that are new or not deleted are returned
          if (!existingPortId || !deletedPortIds.has(existingPortId)) {
            returnedValue.ports.push({
              id: orderToPortIdMap[group.order] ?? null,
              names: {
                [Language.EN]: values[group.nameEnglishKey],
                [Language.GA]: values[group.nameIrishKey],
              },
            });
          }
        });

        onSubmit(returnedValue, callback);
      },
      onEditionCancel: onCancel,
      validate: (values, setError) => {
        const {
          deletedPortIdsString,
          islandNameEnglish,
          islandNameIrish,
          ...portValues
        } = values;
        const deletedPortIds = new Set(deletedPortIdsString.split(';'));

        if (islandNameEnglish.trim().length === 0) {
          setError('islandNameEnglish', t('validation.required-field'));
        }
        if (islandNameIrish.trim().length === 0) {
          setError('islandNameIrish', t('validation.required-field'));
        }

        const uniquePortNames: Map<string, number> = new Map();
        Object.entries(portValues).forEach(([key, value]) => {
          const { order } = parsePortValueKey(key);

          // Ignores deleted exsting ports
          if (deletedPortIds.has(orderToPortIdMap[order])) {
            return;
          }

          const trimmedValue = value.trim();
          if (trimmedValue.length === 0) {
            setError(key, t('validation.required-field'));
          } else {
            if (!uniquePortNames.has(trimmedValue)) {
              uniquePortNames.set(trimmedValue, order);
            }
            if (uniquePortNames.get(trimmedValue) !== order) {
              setError(key, t('validation.duplicated-value'));
            }
          }
        });
      },
      resetOnInitialValuesChange: true,
    },
    { shouldInputsBeDisabled },
  );

  const { deletedPortIdsString, ...portValues } = formState.values;

  const portValuesKeys = Object.keys(portValues).join(',');

  const groupedPortValues = useMemo(() => {
    if (!portValuesKeys) {
      return [];
    }

    return groupPortValueKeys(formState.values).sort(
      (a, b) => a.order - b.order,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portValuesKeys]);

  const deletedPortIds = new Set(deletedPortIdsString.split(';'));

  useEffect(() => {
    onIslandNameChange?.(
      language === Language.GA
        ? formState.values.islandNameIrish.trim()
        : formState.values.islandNameEnglish.trim(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    language,
    formState.values.islandNameEnglish,
    formState.values.islandNameIrish,
  ]);

  return (
    <CommonMainPanelForm
      onCancelEdition={formState.cancelEdition}
      onEditionRequest={formState.requestEdition}
      onSubmit={formState.handleSubmit}
      isEditDisabled={shouldInputsBeDisabled}
      isSubmitDisabled={
        shouldInputsBeDisabled ||
        formState.hasVisibleErrors ||
        !formState.haveValuesChanged
      }
      isEditing={formState.isEditing}
      isSubmitting={shouldInputsBeDisabled}
      className='pr-8'
    >
      <div className="block mb-4">
        <InputHeader required>{t('content.island-name')}</InputHeader>
        <div className="grid grid-cols-2 gap-4">
          <label className={language === Language.GA ? 'col-start-2' : undefined}>
            <header className="text-xs">{t('content.english')}</header>
            <TextField
              {...getTextFieldProps('islandNameEnglish')}
              placeholder={t('content.name')}
            />
          </label>
          <label className={language === Language.GA ? 'row-start-1 col-start-1' : undefined}>
            <header className="text-xs">{t('content.irish')}</header>
            <TextField
              {...getTextFieldProps('islandNameIrish')}
              placeholder={t('content.name')}
            />
          </label>
        </div>
      </div>
      <div className="block mb-4">
        <InputHeader required>{t('content.island-ports')}</InputHeader>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <span
            className={language === Language.GA ? 'col-start-2' : undefined}
          >
            {t('content.english')}
          </span>
          <span
            className={
              language === Language.GA ? 'row-start-1 col-start-1' : undefined
            }
          >
            {t('content.irish')}
          </span>
        </div>
        {groupedPortValues.map(group => {
          const existingPortId = orderToPortIdMap[group.order];
          const isDeleted = deletedPortIds.has(existingPortId);
          const canBeDeleted =
            !isDeleted && Boolean(initialValue || groupedPortValues.length > 1);
          return (
            <div
              key={`port-${group.order}`}
              className={`grid grid-cols-2 gap-4 mb-4 relative`}
            >
              <label
                className={[
                  isDeleted && 'opacity-50',
                  language === Language.GA && 'col-start-2',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <TextField
                  {...getTextFieldProps(group.nameEnglishKey)}
                  disabled={
                    getBaseProps(group.nameEnglishKey).disabled || isDeleted
                  }
                  placeholder={t('content.name')}
                />
              </label>
              <label
                className={[
                  isDeleted && 'opacity-50',
                  language === Language.GA && 'row-start-1 col-start-1',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <TextField
                  {...getTextFieldProps(group.nameIrishKey)}
                  disabled={
                    getBaseProps(group.nameIrishKey).disabled || isDeleted
                  }
                  placeholder={t('content.name')}
                />
              </label>
              {formState.isEditing && canBeDeleted && (
                <div className="absolute right-0 translate-x-[100%]">
                  <Tooltip title={t(`content.delete`)}>
                    <IconButton
                      onClick={() => {
                        if (!existingPortId) {
                          formState.removeFields(
                            group.nameEnglishKey,
                            group.nameIrishKey,
                          );
                        } else {
                          formState.updateValues({
                            deletedPortIdsString: [
                              ...formState.values.deletedPortIdsString.split(
                                ';',
                              ),
                              existingPortId,
                            ].join(';'),
                          });
                        }
                      }}
                      disabled={shouldInputsBeDisabled}
                    >
                      <SvgIcon component={RiDeleteBinLine} />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              {formState.isEditing && isDeleted && (
                <div className="absolute right-0 translate-x-[100%]">
                  <Tooltip title={t(`content.restore`)}>
                    <IconButton
                      onClick={() => {
                        formState.updateValues({
                          deletedPortIdsString:
                            formState.values.deletedPortIdsString
                              .split(';')
                              .filter(x => x !== existingPortId)
                              .join(';'),
                        });
                      }}
                      disabled={shouldInputsBeDisabled}
                    >
                      <SvgIcon component={RiArrowGoBackLine} />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
        {formState.isEditing && (
          <Button
            onClick={() => {
              const newOrderIndex = orderMaxIndex.current;
              formState.addFields({
                [createPortValueKey(newOrderIndex, 'nameEnglish')]: '',
                [createPortValueKey(newOrderIndex, 'nameIrish')]: '',
              });
              orderMaxIndex.current = newOrderIndex + 1;
            }}
            startIcon={<SvgIcon component={RiAddLine} />}
            size="small"
          >
            {t('content.add')}
          </Button>
        )}
      </div>
    </CommonMainPanelForm>
  );
};

export default IslandForm;
